import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  ProviderSearchResult,
  ProviderSearchSelect,
} from '../../../components/provider-search';
import { CustomFieldValue } from '../../../types';

import { COVERAGE_TYPE } from './coverageTypes';

interface CrossCoverageSickSelectProps {
  field: CustomFieldValue | undefined;
  onChange: (field: CustomFieldValue) => void;
  fieldId?: string;
  scope?: 'clinic' | 'market';
}

export const CrossCoverageSickSelect: FC<CrossCoverageSickSelectProps> = ({
  field,
  onChange,
  fieldId,
  scope = 'clinic',
}) => {
  const anyoneLabel =
    scope === 'market' ? 'Anyone in Market' : 'Anyone in Clinic';
  const currentValue = (() => {
    if (!field) {
      return '';
    }
    if (
      field.value === undefined ||
      field.value === null ||
      field.value === ''
    ) {
      return '';
    }
    if (typeof field.value === 'string') {
      return field.value;
    }
    if (typeof field.value === 'object') {
      return '';
    }
    return String(field.value);
  })();

  const currentProviders = (() => {
    if (field?.metadata?.providers) {
      // Convert from existing format to new format
      return (
        field.metadata.providers as Array<{
          providerId?: string;
          id?: string;
          providerLabel?: string;
          label?: string;
          specialty?: string;
          practice?: string;
        }>
      )
        .map((provider) => ({
          id: provider.providerId || provider.id || '',
          label: provider.providerLabel || provider.label || '',
          specialty: provider.specialty || '',
          practice: provider.practice || '',
        }))
        .filter((provider) => provider.id);
    }
    return [];
  })();
  const currentCoverageType =
    (field?.metadata?.coverageType as string) || '';

  // Local state for radio selection and free text option
  const [currentRadioValue, setCurrentRadioValue] = useState<string>(
    () => {
      // Ensure we always have a valid string value
      if (
        currentValue &&
        typeof currentValue === 'string' &&
        currentValue.trim() !== ''
      ) {
        return currentValue;
      }
      return '';
    }
  );
  const [freeTextWithProviderSearch, setFreeTextWithProviderSearch] =
    useState<boolean>(() => {
      // Initialize based on whether providers exist
      return currentProviders.length > 0;
    });
  const [freeTextInstructions, setFreeTextInstructions] =
    useState<string>(
      (field?.metadata?.freeTextInstructions as string) || ''
    );

  // Update field value and metadata
  const updateField = useCallback(
    (
      value: string,
      coverageType: string,
      providers: ProviderSearchResult[] = [],
      instructions: string = ''
    ) => {
      // Convert new format back to existing format for compatibility
      const convertedProviders = providers.map((provider) => ({
        providerId: provider.id,
        providerLabel: provider.label,
        specialty: provider.specialty,
        practice: provider.practice,
      }));

      // Ensure we preserve all required IDs - these are critical for backend operations
      const updatedField: CustomFieldValue = {
        // Preserve existing IDs - these are critical for backend operations
        id: field?.id,
        fieldId: field?.fieldId || fieldId || '', // Use prop fieldId as fallback
        rowId: field?.rowId || null,
        startDate: field?.startDate || null,
        endDate: field?.endDate || null,
        // Update value and metadata - value should be the string value, not the entire object
        value: value,
        metadata:
          value === 'None'
            ? {} // Empty object for None, not null
            : {
                providers: convertedProviders,
                coverageType: coverageType,
                ...(value === 'Free Text' && {
                  freeTextInstructions: instructions,
                }),
              },
      };
      onChange(updatedField);
    },
    [field, onChange, fieldId]
  );

  // Handle radio button changes
  const handleRadioChange = useCallback(
    (value: string) => {
      setCurrentRadioValue(value);

      if (value === anyoneLabel) {
        updateField(anyoneLabel, COVERAGE_TYPE.ANYONE_MARKET, [], '');
        setFreeTextInstructions('');
        setFreeTextWithProviderSearch(false);
      } else if (value === 'Free Text') {
        updateField(
          'Free Text',
          COVERAGE_TYPE.FREE_TEXT,
          [],
          freeTextInstructions
        );
      } else if (value === 'Preferred Provider') {
        updateField(
          'Preferred Provider',
          COVERAGE_TYPE.PREFERRED_PROVIDER,
          currentProviders,
          ''
        );
        setFreeTextInstructions('');
        setFreeTextWithProviderSearch(false);
      }
    },
    [anyoneLabel, updateField, currentProviders, freeTextInstructions]
  );

  // Handle provider changes
  const handleProvidersChange = useCallback(
    (providers: ProviderSearchResult[]) => {
      updateField(
        currentRadioValue,
        currentCoverageType,
        providers,
        freeTextInstructions
      );
    },
    [
      updateField,
      currentRadioValue,
      currentCoverageType,
      freeTextInstructions,
    ]
  );

  // Handle free text with provider search toggle
  const handleFreeTextWithProviderSearchToggle = useCallback(
    (isChecked: boolean) => {
      setFreeTextWithProviderSearch(isChecked);
      if (isChecked) {
        // Keep existing providers if any
        updateField(
          'Free Text',
          'free_text',
          currentProviders,
          freeTextInstructions
        );
      } else {
        // Remove providers, keep free text
        updateField(
          'Free Text',
          'free_text',
          [],
          freeTextInstructions
        );
      }
    },
    [updateField, currentProviders, freeTextInstructions]
  );

  // Handle free text instructions changes
  const handleFreeTextInstructionsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const instructions = e.target.value;
      setFreeTextInstructions(instructions);
      updateField(
        currentRadioValue,
        currentCoverageType,
        currentProviders,
        instructions
      );
    },
    [
      updateField,
      currentRadioValue,
      currentCoverageType,
      currentProviders,
    ]
  );

  // Determine if provider search should be shown
  const showProviderSearch =
    currentRadioValue === 'Preferred Provider' ||
    (currentRadioValue === 'Free Text' && freeTextWithProviderSearch);

  // Sync field prop changes to local state
  useEffect(() => {
    if (!field) return;
    const coverageType =
      (field.metadata?.coverageType as string) || '';
    // coverageType is source of truth; value is content only
    const mode =
      coverageType === COVERAGE_TYPE.PREFERRED_PROVIDER
        ? 'Preferred Provider'
        : coverageType === COVERAGE_TYPE.ANYONE_MARKET
        ? anyoneLabel
        : coverageType === COVERAGE_TYPE.FREE_TEXT
        ? 'Free Text'
        : (() => {
            const v =
              typeof field.value === 'string'
                ? field.value.trim()
                : '';
            if (v === 'Preferred Provider' || v === 'Free Text') return v;
            if (v === 'Anyone in Clinic' || v === 'Anyone in Market')
              return anyoneLabel;
            const legacyProviders = field.metadata?.providers;
            return (v && v !== 'None')
              ? 'Free Text'
              : Array.isArray(legacyProviders) && (legacyProviders as unknown[]).length > 0
              ? 'Preferred Provider'
              : '';
          })();

    setCurrentRadioValue(mode);

    if (mode === 'Free Text') {
      const instructions = field.metadata?.freeTextInstructions;
      setFreeTextInstructions(
        typeof instructions === 'string' ? instructions : ''
      );
      const providers = field.metadata?.providers;
      setFreeTextWithProviderSearch(
        (prev) =>
          prev || (Array.isArray(providers) && providers.length > 0)
      );
    } else {
      setFreeTextInstructions('');
      setFreeTextWithProviderSearch(false);
    }
  }, [field, anyoneLabel]);

  return (
    <Box>
      {/* Radio Selection */}
      <RadioGroup
        value={currentRadioValue}
        onChange={handleRadioChange}
        mb="1.5rem"
      >
        <VStack spacing={4} align="start" width="100%">
          <Box
            p="1rem 1.25rem"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            cursor="pointer"
            width="100%"
            _hover={{
              bg: 'gray.50',
              borderColor: 'gray.300',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
            onClick={() => handleRadioChange('Preferred Provider')}
          >
            <Radio
              value="Preferred Provider"
              size="md"
              pointerEvents="none"
            >
              <Box>
                <Text fontWeight="600" color="gray.700">
                  Preferred Provider
                </Text>
                <Text fontSize="0.75rem" color="gray.500">
                  Specific providers are preferred for coverage
                </Text>
              </Box>
            </Radio>
          </Box>

          <Box
            p="1rem 1.25rem"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            cursor="pointer"
            width="100%"
            _hover={{
              bg: 'gray.50',
              borderColor: 'gray.300',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
            onClick={() => handleRadioChange(anyoneLabel)}
          >
            <Radio
              value={anyoneLabel}
              size="md"
              pointerEvents="none"
            >
              <Box>
                <Text fontWeight="600" color="gray.700">
                  {anyoneLabel}
                </Text>
                <Text fontSize="0.75rem" color="gray.500">
                  {scope === 'market'
                    ? 'Any available provider in this market can provide coverage'
                    : 'Any available provider in this clinic can provide coverage'}
                </Text>
              </Box>
            </Radio>
          </Box>

          <Box
            p="1rem 1.25rem"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            cursor="pointer"
            width="100%"
            _hover={{
              bg: 'gray.50',
              borderColor: 'gray.300',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
            onClick={() => handleRadioChange('Free Text')}
          >
            <Radio value="Free Text" size="md" pointerEvents="none">
              <Box>
                <Text fontWeight="600" color="gray.700">
                  Free Text
                </Text>
                <Text fontSize="0.75rem" color="gray.500">
                  Custom coverage instructions
                </Text>
              </Box>
            </Radio>
          </Box>
        </VStack>
      </RadioGroup>

      {/* Free Text with Provider Search Option */}
      {currentRadioValue === 'Free Text' && (
        <Box
          mb="1rem"
          p="1rem"
          bg="blue.50"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
        >
          <Checkbox
            isChecked={freeTextWithProviderSearch}
            onChange={(e) =>
              handleFreeTextWithProviderSearchToggle(e.target.checked)
            }
            colorScheme="blue"
            size="md"
          >
            <Text fontSize="sm" fontWeight="500" color="blue.700">
              Also search and select specific providers for coverage?
            </Text>
          </Checkbox>
          <Text fontSize="0.75rem" color="blue.600" mt={1} ml={6}>
            This will allow you to specify both free text instructions
            and preferred providers
          </Text>
        </Box>
      )}

      {/* Provider Search Section - Show for Preferred Provider or Free Text with provider search */}
      <Collapse in={showProviderSearch} animateOpacity>
        <Box
          p="1.5rem"
          bg="gray.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          mb="1.5rem"
          _hover={{
            borderColor: 'gray.300',
            boxShadow: 'sm',
          }}
          transition="all 0.2s"
        >
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.700"
            mb="1rem"
          >
            Search and Select Providers
          </Text>

          <ProviderSearchSelect
            onProvidersChange={handleProvidersChange}
            selectedProviders={currentProviders}
            placeholder="Search for providers by name or specialty..."

          />

          {/* Validation Warning */}
          {currentRadioValue === 'Preferred Provider' &&
            currentProviders.length === 0 && (
              <Alert status="warning" borderRadius="md" mt="1rem">
                <AlertIcon />
                <Box>
                  <AlertTitle>Provider Selection Required</AlertTitle>
                  <AlertDescription>
                    Please select at least one provider for preferred
                    provider coverage.
                  </AlertDescription>
                </Box>
              </Alert>
            )}
        </Box>
      </Collapse>

      {/* Free Text Instructions Input Field */}
      {currentRadioValue === 'Free Text' && (
        <Box
          p="1.5rem"
          bg="gray.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          mb="1.5rem"
          _hover={{
            borderColor: 'gray.300',
            boxShadow: 'sm',
          }}
          transition="all 0.2s"
        >
          <FormControl>
            <FormLabel
              fontSize="sm"
              fontWeight="600"
              color="gray.700"
              mb="0.75rem"
            >
              Free Text Instructions
            </FormLabel>
            <Input
              value={freeTextInstructions}
              onChange={handleFreeTextInstructionsChange}
              placeholder="e.g., Refer to on-call schedule, contact Dr. Doe directly, or follow emergency protocol"
              size="md"
              _hover={{ borderColor: 'gray.400' }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
            />
            <Text fontSize="xs" color="gray.500" mt="0.5rem">
              Specify any additional instructions for cross coverage
              when this provider is unavailable.
            </Text>
          </FormControl>
        </Box>
      )}

      {/* Coverage Type Display */}
      <Box
        p="1rem"
        bg="green.50"
        borderRadius="md"
        border="1px solid"
        borderColor="green.200"
        _hover={{
          borderColor: 'green.300',
          boxShadow: 'sm',
          transform: 'translateY(-1px)',
        }}
        transition="all 0.2s"
      >
        <Text fontSize="sm" fontWeight="500" color="green.700">
          Coverage Type:{' '}
          <Text as="span" fontWeight="600">
            {currentRadioValue}
          </Text>
        </Text>
        {currentProviders.length > 0 && (
          <Text fontSize="sm" color="green.600" mt={1}>
            {currentProviders.length} provider
            {currentProviders.length !== 1 ? 's' : ''} selected
          </Text>
        )}
      </Box>
    </Box>
  );
};
